import axios, { type AxiosInstance, type AxiosResponse, type AxiosError } from 'axios';
import { API_BASE_URL } from '../config/api';
import { AuthService } from '../auth';
import { errorService } from '../services/errorService';
import { ErrorSeverity } from '../types/error';

/**
 * Create axios instance with base configuration
 */
const createHttpClient = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 60000, // 60s timeout for Azure cold start + large queries
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor to add Authorization header and handle FormData
  instance.interceptors.request.use(
    (config: any) => {
      const token = AuthService.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // Let Axios set Content-Type automatically for FormData (multipart/form-data)
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
      }
      return config;
    },
    (error: any) => {
      // Handle request errors
      const appError = errorService.handleError(error, {
        severity: ErrorSeverity.MEDIUM,
        context: { source: 'request-interceptor' },
        userMessage: 'Failed to send request',
      });
      return Promise.reject(appError);
    }
  );

  // Response interceptor to handle errors with centralized error service
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    (error: AxiosError) => {
      // Handle network/HTTP errors using the error service
      const networkError = errorService.handleNetworkError(error, {
        context: { source: 'response-interceptor' },
        showToUser: true,
      });

      // Handle 401 Unauthorized specifically
      if (error.response?.status === 401) {
        console.log('401 Unauthorized - clearing auth and redirecting to login');

        // Handle auth error
        errorService.handleAuthError('Session expired. Please log in again.', {
          context: {
            source: 'http-interceptor',
            url: error.config?.url,
            method: error.config?.method
          },
        });

        // Clear auth using AuthService
        AuthService.logout();

        // Use window.location to redirect to avoid circular dependencies
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }

      return Promise.reject(networkError);
    }
  );

  return instance;
};

// Export the configured axios instance
export const http = createHttpClient();

export default http;